import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { EntryList } from "../../components/EntryList.js";
import { MemberPicker } from "../../components/MemberPicker.js";
import { Button, Chips, Field, Notice, Selector } from "../../components/ui.js";
import { hasRole, useAuth } from "../../lib/auth.js";
import { addManagedMember, fetchMembers } from "../../lib/members.js";
import { addRegistration, fetchEventContacts, removeRegistration } from "../../lib/tournaments.js";
import {
  entryRefusal,
  eventGender,
  isKidsCategory,
  registeredEntries,
  type Member,
  type MemberCategory,
  type RegistrationStatus,
  type Tournament,
  type TournamentEvent,
} from "../../lib/types.js";
import { EventGate, EventScreen, useEventContacts, useEventNotes } from "./shared.js";

/**
 * Who is in, who is waiting, and — if you organise — what they said about it.
 *
 * One screen for both, rather than a player's list here and an organiser's list somewhere
 * else. The difference between the two is what the database will hand over: `event_notes`
 * gives an organiser everyone's note and gives everybody else their own. Duplicating the
 * list to express that would be two lists to keep in step over one privilege check.
 *
 * Contact details are the organiser's alone — `event_contacts` answers nobody else — so they
 * are asked for only when they would be answered.
 */
export function EventEntries() {
  return (
    <EventGate>
      {({ tournament, event }) => <Entries tournament={tournament} event={event} />}
    </EventGate>
  );
}

function Entries({ tournament, event }: { tournament: Tournament; event: TournamentEvent }) {
  const { member } = useAuth();
  const memberId = member?.id ?? null;
  const canOrganise = hasRole(member, "organiser");
  const queryClient = useQueryClient();

  const { data: notes } = useEventNotes(event.id, Boolean(memberId));
  const { data: contacts } = useEventContacts(event.id, canOrganise);

  const remove = useMutation({
    mutationFn: (target: string) => removeRegistration(event.id, target),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tournaments"] });
      void queryClient.invalidateQueries({ queryKey: ["event-notes", event.id] });
      void queryClient.invalidateQueries({ queryKey: ["event-contacts", event.id] });
      // Somebody taken out may be on a team sheet, where they now read as withdrawn.
      void queryClient.invalidateQueries({ queryKey: ["tie-teams", event.id] });
    },
  });

  const entered = registeredEntries(event.entries).length;
  const waiting = event.entries.length - entered;

  return (
    <EventScreen event={event} title="Who's in">
      <p className="micro -mt-4 mb-6">
        {entered} in{event.capacity ? ` of ${event.capacity}` : ""}
        {waiting > 0 ? ` · ${waiting} waiting` : ""}
      </p>

      <EntryList
        entries={event.entries}
        notes={notes}
        contacts={canOrganise ? contacts : undefined}
        memberId={memberId}
        showNoteCount={canOrganise}
        onRemove={canOrganise ? (target) => remove.mutate(target) : undefined}
        removing={remove.isPending}
      />

      {remove.error ? (
        <p className="-mt-6 text-sm" style={{ color: "var(--color-attention)" }}>
          {remove.error.message}
        </p>
      ) : null}

      {canOrganise ? (
        <AddSomebody event={event} contactRequired={tournament.contactRequired} />
      ) : null}

      {!event.entries.some((entry) => entry.memberId === memberId) ? (
        <Link to={`/events/${event.id}/register`} className="micro underline">
          Register for this event
        </Link>
      ) : null}
    </EventScreen>
  );
}

/**
 * The organiser putting somebody in.
 *
 * Half the club tells the organiser they are coming rather than tapping anything, and after
 * the cutoff that is the only way in — which is the point of a cutoff that an organiser can
 * still reach through. `add_registration` audits what it let past: the closed doors, and a
 * division band the player is outside of.
 *
 * Capacity is not let past. Somebody added to a full event waits like anybody else, and the
 * result says which happened.
 *
 * Who the event is for is let past too (20260930000100), and the result says so: an adult put
 * into boys' singles is in, the audit entry carries `category_override`, and the organiser is
 * told it was an override rather than finding out from the entry list looking wrong.
 *
 * So are missing contact details, where the tournament asks for them (20261001000300). The
 * add's result does not say — `add_registration` keeps its signature — so the contacts are
 * fetched again afterwards, which also puts the new entrant's on the list.
 */
interface NewChild {
  name: string;
  guardian: string;
  category: MemberCategory | null;
}

interface Added {
  name: string;
  status: RegistrationStatus;
  override: string | null;
  missingContact: boolean;
}

function AddSomebody({
  event,
  contactRequired,
}: {
  event: TournamentEvent;
  contactRequired: boolean;
}) {
  const queryClient = useQueryClient();
  const [added, setAdded] = useState<Added | null>(null);
  // A child being created: the typed name, boy or girl, and who looks after them if anybody.
  const [newChild, setNewChild] = useState<NewChild | null>(null);
  // A kids' event has already said boy or girl; anything else leaves it to the organiser.
  const presetGender = isKidsCategory(event.category) ? eventGender(event.category) : null;

  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: fetchMembers });

  // Refetched whether or not the tournament asks, so the new entrant's details are on the list;
  // only read for the notice where it does. `staleTime: 0` because the client's default would
  // hand back the map from before the add, which does not have them in it.
  const missingContact = async (memberId: string) => {
    const contacts = await queryClient.fetchQuery({
      queryKey: ["event-contacts", event.id],
      queryFn: () => fetchEventContacts(event.id),
      staleTime: 0,
    });
    return contactRequired && (contacts.get(memberId)?.missing ?? false);
  };

  const add = useMutation({
    mutationFn: async (member: Member): Promise<Added> => {
      const status = await addRegistration(event.id, member.id);
      return {
        name: member.displayName,
        status,
        override: entryRefusal(event.category, member),
        missingContact: await missingContact(member.id),
      };
    },
    onSuccess: (result) => {
      setAdded(result);
      void queryClient.invalidateQueries({ queryKey: ["tournaments"] });
    },
  });

  // Two calls rather than one RPC. If the second fails the child exists and is not entered,
  // which is harmless — they are in the list next time — and a combined function would be a
  // second copy of add_registration's rules. See design §4 of add-kids-registration.
  const create = useMutation({
    mutationFn: async (child: NewChild): Promise<Added> => {
      const id = await addManagedMember(child.name, child.category!, child.guardian || null);
      const status = await addRegistration(event.id, id);
      return {
        name: child.name.trim(),
        status,
        override: entryRefusal(event.category, {
          category: child.category,
          junior: true,
          senior: false,
        }),
        missingContact: await missingContact(id),
      };
    },
    onSuccess: (result) => {
      setAdded(result);
      setNewChild(null);
      void queryClient.invalidateQueries({ queryKey: ["tournaments"] });
      void queryClient.invalidateQueries({ queryKey: ["members"] });
    },
  });

  const already = event.entries
    .filter((entry) => entry.status !== "withdrawn")
    .map((entry) => entry.memberId);

  const guardianOptions = members
    .filter((m) => m.status === "active" && !m.managed)
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  return (
    <section className="mb-8 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
      <h2 className="micro mb-3">Add somebody</h2>

      {newChild ? (
        <div className="mb-4">
          <p className="mb-3">
            Add <strong>{newChild.name}</strong> as a child without a login, and enter them.
          </p>
          {/* A div, not a Field: Field is a <label>, and a label around buttons lends its
              text to the first one, so "Boy" would stop being called Boy. */}
          <div className="mb-6" role="group" aria-labelledby="new-child-gender">
            <span id="new-child-gender" className="field-label">
              Boy or girl
            </span>
            <Chips
              options={[
                { value: "men" as const, label: "Boy" },
                { value: "women" as const, label: "Girl" },
              ]}
              selected={newChild.category ? [newChild.category] : []}
              onToggle={(category) => setNewChild({ ...newChild, category })}
            />
          </div>
          <Field
            label="Looked after by"
            hint="Optional. A guardian can enter them in events and fix their name."
          >
            <Selector
              label="Guardian"
              value={newChild.guardian}
              onChange={(guardian) => setNewChild({ ...newChild, guardian })}
              options={[
                { value: "", label: "Nobody yet" },
                ...guardianOptions.map((m) => ({ value: m.id, label: m.displayName })),
              ]}
            />
          </Field>
          <div className="flex flex-wrap gap-2">
            <Button
              tone="primary"
              onClick={() => create.mutate(newChild)}
              disabled={create.isPending || newChild.category === null}
            >
              {create.isPending ? "Adding…" : `Add and enter ${newChild.name}`}
            </Button>
            <Button onClick={() => setNewChild(null)} disabled={create.isPending}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <MemberPicker
          label="Who is playing"
          members={members}
          chosen={already}
          onPick={(memberId) => {
            const member = members.find((candidate) => candidate.id === memberId);
            if (member) add.mutate(member);
          }}
          onCreate={(name) => setNewChild({ name, guardian: "", category: presetGender })}
          createLabel="as a new child"
        />
      )}

      {add.isPending ? <p className="micro">Adding…</p> : null}

      {added ? (
        <Notice tone={added.override || added.missingContact ? "attention" : "muted"}>
          {added.name} is{" "}
          {added.status === "registered" ? "in" : "on the waitlist — the event is full"}.
          {added.override
            ? ` Added as an organiser's override: ${added.override}. It's on the record.`
            : null}
          {added.missingContact ? " They don't have a flat number and phone number on file." : null}
        </Notice>
      ) : null}

      {add.error ? <Notice tone="attention">{add.error.message}</Notice> : null}
      {create.error ? <Notice tone="attention">{create.error.message}</Notice> : null}
    </section>
  );
}
